import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Users, Calendar } from 'lucide-react';
import { Quote } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const Home = () => {
  const [heroRef, heroVisible] = useScrollAnimation(0.1);
  const [welcomeRef, welcomeVisible] = useScrollAnimation(0.2);
  const [featuresRef, featuresVisible] = useScrollAnimation(0.2);
  const [galleryRef, galleryVisible] = useScrollAnimation(0.2);
  const [ctaRef, ctaVisible] = useScrollAnimation(0.2);

  return (
    <div className="min-h-screen bg-cream page-transition">
      {/* Hero Section with Real Photo */}
      <section 
        ref={heroRef}
        className="relative h-screen flex items-center justify-center text-white overflow-hidden"
      >
        <div 
          className="absolute inset-0 hero-bg-zoom"
          style={{
            backgroundImage: "url('https://customer-assets.emergentagent.com/job_client-web-setup/artifacts/5jlps656_IMG_0092.jpeg')",
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="absolute inset-0 image-overlay-luxury"></div>
        <div className={`relative z-10 text-center px-4 max-w-5xl mx-auto ${heroVisible ? 'animate-fade-in-up' : 'opacity-0'}`} data-testid="hero-section">
          <h1 className={`text-4xl md:text-6xl font-bold mb-8 text-shadow-luxury ${heroVisible ? 'animate-fade-in-up' : 'opacity-0'}`} data-testid="hero-title" style={{fontFamily: "'Cormorant Garamond', Georgia, serif", lineHeight: '1.2'}}>
            A place where food brings people together.
          </h1>
          <p className={`text-xl md:text-3xl mb-10 font-light text-shadow-luxury max-w-4xl mx-auto ${heroVisible ? 'animate-fade-in-up delay-200' : 'opacity-0'}`} data-testid="hero-tagline" style={{lineHeight: '1.5'}}>
            Join Mama-Mam's Kitchen for a warm Sunday experience filled with home-cooked food, music, and connection.
          </p>
          <Link 
            to="/reserve" 
            className={`btn-luxury inline-block text-xl font-bold transform shadow-2xl ${heroVisible ? 'animate-scale-in delay-400' : 'opacity-0'}`}
            data-testid="hero-cta-button"
          >
            Book Your Sunday Spot →
          </Link>
        </div>
      </section>

      {/* Welcome Section */}
      <section ref={welcomeRef} className="py-24 px-4 bg-cream-dark" data-testid="welcome-section">
        <div className={`max-w-4xl mx-auto text-center ${welcomeVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
          <div className="w-16 h-1 bg-gold mx-auto mb-8"></div>
          <h2 className="text-5xl md:text-6xl font-bold text-brown mb-8" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>
            Welcome to Mama-Mam's Kitchen
          </h2>
          <p className={`text-2xl text-gray-700 leading-relaxed mb-6 italic ${welcomeVisible ? 'animate-fade-in-up delay-200' : 'opacity-0'}`}>
            Where every dish tells a story, and every table feels like family.
          </p>
          <p className={`text-lg text-gray-600 leading-relaxed mb-6 ${welcomeVisible ? 'animate-fade-in-up delay-300' : 'opacity-0'}`}>
            At Mama-Mam's, we believe that food tastes better when shared. That's why we've created an elegant, buffet-style dining experience where friends and families can come together to enjoy authentic Afro Caribbean cuisine made with love — honouring the legacy of <strong>Mama-Mam</strong>, who taught us to keep family together, spreading love around the dinner table.
          </p>
          <p className={`text-lg text-gray-600 leading-relaxed ${welcomeVisible ? 'animate-fade-in-up delay-400' : 'opacity-0'}`}>
            Join us this Sunday and rediscover the joy of gathering around exquisite food, laughter, and love.
          </p>
        </div>
      </section>

      {/* Real Event Photo Showcase */}
      <section ref={galleryRef} className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          {/* Testimonial After Welcome */}
          <div className={`max-w-3xl mx-auto mb-16 ${galleryVisible ? 'animate-scale-in' : 'opacity-0'}`}>
            <div className="bg-gold-light p-12 rounded-lg shadow-luxury-hover border-l-4 border-gold hover-lift">
              <Quote className="w-12 h-12 text-gold mb-4 mx-auto animate-pulse-slow" />
              <p className="text-2xl text-gray-700 italic leading-relaxed mb-6 text-center">
                "Aunty Ange you done soooo well, literally the cutest day, thank you for having us, everything was perfect, from the laughter, dances, food, energy... top tier lux event!"
              </p>
              <p className="font-bold text-brown text-xl text-center">— Anneka</p>
            </div>
          </div>

          {/* Photo Gallery Grid */}
          <div className={`grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8 ${galleryVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
            <div className="relative rounded-lg shadow-luxury lg:col-span-2 image-hover-zoom">
              <img 
                src="https://customer-assets.emergentagent.com/job_client-web-setup/artifacts/oq9hbmvg_IMG_0129.jpeg" 
                alt="Families celebrating together at Mama-Mam's Kitchen" 
                className="w-full h-96 object-cover rounded-lg"
              />
            </div>
            <div className="relative rounded-lg shadow-luxury image-hover-zoom">
              <img 
                src="https://customer-assets.emergentagent.com/job_client-web-setup/artifacts/bnor1795_IMG_0105.jpeg" 
                alt="Enjoying a meal together" 
                className="w-full h-96 object-cover rounded-lg"
              />
            </div>
          </div>

          <div className={`grid md:grid-cols-2 gap-8 ${galleryVisible ? 'animate-fade-in-up delay-200' : 'opacity-0'}`}>
            <div className="relative rounded-lg shadow-luxury image-hover-zoom">
              <img 
                src="https://customer-assets.emergentagent.com/job_client-web-setup/artifacts/tqhnalfr_IMG_0101.jpeg" 
                alt="Community gathering at Mama-Mam's" 
                className="w-full h-96 object-cover rounded-lg"
              />
            </div>
            <div className="relative rounded-lg shadow-luxury image-hover-zoom">
              <img 
                src="https://customer-assets.emergentagent.com/job_client-web-setup/artifacts/jz2vaed1_IMG_0100.jpeg" 
                alt="Authentic Caribbean buffet spread" 
                className="w-full h-96 object-cover rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-4 bg-cream-dark">
        <div className="max-w-6xl mx-auto">
          <div className="w-16 h-1 bg-gold mx-auto mb-12"></div>
          <div className="grid md:grid-cols-3 gap-12">
            <div className="text-center bg-white p-10 rounded-lg shadow-luxury-hover transition-all" data-testid="feature-family">
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-gold-light rounded-full flex items-center justify-center">
                  <Users className="w-10 h-10 text-brown" />
                </div>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-brown" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>Keep Family Tight</h3>
              <p className="text-gray-600 leading-relaxed">
                An elegant, welcoming space where everyone is treated like family. Just like <strong>Mama-Mam</strong> taught us.
              </p>
            </div>
            <div className="text-center bg-white p-10 rounded-lg shadow-luxury-hover transition-all" data-testid="feature-food">
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-gold-light rounded-full flex items-center justify-center">
                  <Heart className="w-10 h-10 text-brown" />
                </div>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-brown" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>Spread Love</h3>
              <p className="text-gray-600 leading-relaxed">
                Every dish is prepared from scratch with care, love, and authentic Afro Caribbean recipes passed down through generations.
              </p>
            </div>
            <div className="text-center bg-white p-10 rounded-lg shadow-luxury-hover transition-all" data-testid="feature-buffet">
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-gold-light rounded-full flex items-center justify-center">
                  <Calendar className="w-10 h-10 text-brown" />
                </div>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-brown" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>Sunday Tradition</h3>
              <p className="text-gray-600 leading-relaxed">
                Join us every Sunday for our signature buffet experience. What started as Angella's family tradition is now yours too.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Elegant Table Setting Photo */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="relative overflow-hidden rounded-lg shadow-luxury">
            <img 
              src="https://customer-assets.emergentagent.com/job_client-web-setup/artifacts/hxzyvphj_IMG_0090.jpeg" 
              alt="Elegant table setting at Mama-Mam's" 
              className="w-full h-[500px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent flex items-end">
              <div className="p-12 text-white">
                <h3 className="text-4xl font-bold mb-3" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>Elegant Dining Experience</h3>
                <p className="text-xl">Where luxury meets home comfort</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gold text-white text-center">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-5xl md:text-6xl font-bold mb-6" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>Ready to Experience a Proper Meal?</h2>
          <p className="text-2xl mb-10 leading-relaxed max-w-3xl mx-auto">
            Bring your family or friends, or just yourself, and enjoy a warm, welcoming atmosphere with great food and even better company.
          </p>
          
          <div className="mb-8">
            <Link 
              to="/reserve" 
              className="inline-block bg-white text-brown hover:bg-cream font-bold px-12 py-5 rounded-full text-2xl transition-all transform hover:scale-110 shadow-2xl mr-4 mb-4"
              data-testid="cta-reserve-button"
            >
              Reserve Your Spot Now →
            </Link>
          </div>
          
          {/* Contact Info */}
          <div className="border-t border-white/30 pt-8 mt-8">
            <p className="text-lg mb-4 opacity-90">Connect with us</p>
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <a href="tel:07427056361" className="hover:underline">📞 07427 056361</a>
              <span className="opacity-50">|</span>
              <a href="mailto:mamamamskitchen@gmail.com" className="hover:underline">📧 mamamamskitchen@gmail.com</a>
              <span className="opacity-50">|</span>
              <a href="https://www.instagram.com/mamamamskitchen/" target="_blank" rel="noopener noreferrer" className="hover:underline">📷 @mamamamskitchen</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
